import React, { useRef, useState, useMemo } from 'react';
import { Camera, Loader2, Save, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Input, PhoneInput, Dropdown } from '../../components/forms';
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUploadAvatarMutation,
  useRemoveAvatarMutation,
  UpdateProfileRequest,
} from '../../services/profileApi';
import { useToast } from '../../contexts/ToastContext';
import { getCountryCodes } from '../../utils/phoneValidation';

const COMPANY_TYPES = [
  { value: 'General Contractor', label: 'General Contractor' },
  { value: 'Subcontractor', label: 'Subcontractor' },
  { value: 'Developer', label: 'Developer' },
  { value: 'Architect', label: 'Architect' },
  { value: 'Engineer', label: 'Engineer' },
  { value: 'Property Management', label: 'Property Management' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Other', label: 'Other' },
];

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  companyName: string;
  companyType: string;
}

const ProfileOverview: React.FC = () => {
  const { success, error: showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: profile, isLoading, error } = useGetProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [uploadAvatar] = useUploadAvatarMutation();
  const [removeAvatar, { isLoading: isRemovingAvatar }] = useRemoveAvatarMutation();

  // Get country codes for phone input
  const countryCodes = useMemo(() => getCountryCodes(), []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
      mobileNumber: profile?.mobileNumber || '',
      countryCode: profile?.countryCode || '+1',
      companyName: profile?.companyName || '',
      companyType: profile?.companyType || '',
    },
  });

  // Update form when profile loads
  React.useEffect(() => {
    if (profile) {
      setValue('firstName', profile.firstName || '');
      setValue('lastName', profile.lastName || '');
      setValue('email', profile.email || '');
      setValue('mobileNumber', profile.mobileNumber || '');
      setValue('countryCode', profile.countryCode || '+1');
      setValue('companyName', profile.companyName || '');
      setValue('companyType', profile.companyType || '');
    }
  }, [profile, setValue]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      const updateData: UpdateProfileRequest = {
        firstName: data.firstName,
        lastName: data.lastName,
        mobileNumber: data.mobileNumber,
        countryCode: data.countryCode,
        companyName: data.companyName,
        companyType: data.companyType,
      };

      await updateProfile(updateData).unwrap();
      success('Profile updated successfully');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update profile');
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showError('File is too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      await uploadAvatar(formData).unwrap();
      success('Avatar uploaded successfully');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await removeAvatar().unwrap();
      success('Avatar removed successfully');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to remove avatar');
    }
  };

  const getInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
    }
    if (profile?.name) {
      const parts = profile.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return profile.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load profile. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage your personal information and profile settings
        </p>
      </div>

      {/* Avatar Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-medium text-gray-900 mb-3">Profile Photo</h2>
        <div className="flex items-center space-x-5">
          {/* Avatar */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-brand-50 flex items-center justify-center">
              {profile?.avatar?.url ? (
                <img
                  src={profile.avatar.url}
                  alt={profile.name || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-semibold text-brand-primary">{getInitials()}</span>
              )}
            </div>
            {/* Upload overlay */}
            <button
              type="button"
              onClick={handleAvatarClick}
              disabled={isUploading}
              className="absolute bottom-0 right-0 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white hover:bg-brand-700 transition-colors shadow-lg disabled:opacity-50"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>

          {/* Avatar actions */}
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-3">
              Upload a new avatar. Recommended size: 200x200px. Max file size: 5MB.
            </p>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={isUploading}
                className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Upload New Photo'}
              </button>
              {profile?.avatar?.url && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  disabled={isRemovingAvatar}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isRemovingAvatar ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>Remove</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-sm font-medium text-gray-900 mb-4">Personal Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <Input
              label="First Name"
              {...register('firstName', { required: 'First name is required' })}
              error={errors.firstName?.message}
              placeholder="Enter your first name"
            />

            {/* Last Name */}
            <Input
              label="Last Name"
              {...register('lastName', { required: 'Last name is required' })}
              error={errors.lastName?.message}
              placeholder="Enter your last name"
            />

            {/* Email (read-only) */}
            <div>
              <Input
                label="Email Address"
                {...register('email')}
                disabled
                className="bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                To change your email, go to Security settings.
              </p>
            </div>

            {/* Phone */}
            <PhoneInput
              label="Mobile Number"
              value={watch('mobileNumber') || ''}
              countryCodes={countryCodes}
              selectedCountryCode={watch('countryCode') || '+1'}
              onCountryCodeChange={code => setValue('countryCode', code, { shouldDirty: true })}
              onChange={phone => setValue('mobileNumber', phone, { shouldDirty: true })}
              error={errors.mobileNumber?.message}
            />

            {/* Company Name */}
            <Input
              label="Company Name"
              {...register('companyName')}
              error={errors.companyName?.message}
              placeholder="Enter your company name"
            />

            {/* Company Type */}
            <Dropdown
              label="Company Type"
              options={COMPANY_TYPES}
              value={watch('companyType') || ''}
              onChange={value => setValue('companyType', value as string, { shouldDirty: true })}
              placeholder="Select company type"
            />
          </div>

          {/* Submit Button */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
            <button
              type="submit"
              disabled={!isDirty || isUpdating}
              className="px-6 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Account Info */}
      <div className="mt-4 bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-3">Account Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Account Type</p>
            <p className="text-sm font-medium text-gray-900">
              {profile?.role === 'admin' ? 'Administrator' : 'User'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Email Status</p>
            <p className="text-sm font-medium">
              {profile?.isEmailVerified ? (
                <span className="text-green-600">Verified</span>
              ) : (
                <span className="text-amber-600">Not Verified</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Member Since</p>
            <p className="text-sm font-medium text-gray-900">
              {profile?.createdAt
                ? new Date(profile.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileOverview;
